<template>
  <a-base-component :subscribed_modules="subscribed_modules">
    <template v-slot:default>
      <!-- Filters -->
      <v-card class="mb-4">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" sm="4" md="3">
              <v-menu
                v-model="menu_start"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="start_date"
                    :label="$t('start_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="start_date"
                  @input="menu_start = false"
                  :first-day-of-week="1"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-menu
                v-model="menu_end"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="end_date"
                    :label="$t('end_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="end_date"
                  @input="menu_end = false"
                  :first-day-of-week="1"
                  :min="start_date"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col v-if="isUlysesPms" cols="12" sm="4" md="2">
              <v-select
                v-model="validation_source"
                :items="validation_source_options"
                :label="$t('data_source')"
                dense
                outlined
                hide-details
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="4"
              :md="isUlysesPms ? 1 : 3"
              class="d-flex align-center"
              style="gap: 8px;"
            >
              <v-tooltip bottom dark color="#333333">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="goToPreviousMonth"
                    :disabled="loading"
                    v-on="on"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("previous_month") }}</span>
              </v-tooltip>
              <v-btn
                color="secondary"
                rounded
                depressed
                :loading="loading"
                :disabled="!start_date || !end_date"
                @click="validate"
              >
                <v-icon small class="mr-1">mdi-check-decagram</v-icon>
                {{ $t("validate") }}
              </v-btn>
              <v-tooltip bottom dark color="#333333">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="goToNextMonth"
                    :disabled="loading"
                    v-on="on"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("next_month") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Loading -->
      <v-card v-if="loading">
        <v-card-text>
          <v-progress-linear
            color="secondary"
            indeterminate
          ></v-progress-linear>
        </v-card-text>
      </v-card>

      <!-- Error message -->
      <v-alert
        v-if="error_message && !loading"
        type="error"
        prominent
        outlined
        class="mb-4 mt-15"
      >
        {{ error_message }}
      </v-alert>

      <!-- Results -->
      <div v-if="validation_data && !loading">
        <!-- Period summary -->
        <v-card class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            {{ $t("period_summary") }}: {{ start_date }} → {{ end_date }}
          </v-card-title>
          <v-card-text>
            <div class="d-flex flex-wrap justify-center" style="gap: 8px;">
              <div
                v-for="kpi in kpi_definitions"
                :key="'summary-' + kpi.key"
                class="text-center pa-2"
                style="min-width: 100px;"
              >
                <div class="text-caption font-weight-bold text-uppercase mb-1">
                  {{ $t(kpi.label) }}
                </div>
                <v-tooltip bottom color="#333333">
                  <template v-slot:activator="{ on }">
                    <v-chip
                      small
                      :color="getPeriodMatchColor(kpi.key)"
                      dark
                      v-on="on"
                    >
                      {{ getPeriodMatchLabel(kpi.key) }}
                    </v-chip>
                  </template>
                  <div class="text-center" style="line-height: 1.6;">
                    <div>
                      <strong>RMS:</strong>
                      {{
                        formatKpiValue(kpi, getPeriodValue(kpi.key, "veetal"))
                      }}
                    </div>
                    <div>
                      <strong>PMS:</strong>
                      {{ formatKpiValue(kpi, getPeriodValue(kpi.key, "pms")) }}
                    </div>
                    <div>
                      <strong>{{ $t("difference") }}:</strong>
                      {{ formatKpiValue(kpi, getPeriodDiff(kpi.key)) }}
                    </div>
                  </div>
                </v-tooltip>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- KPI detail tables -->
        <v-card
          v-for="kpi in kpi_definitions"
          :key="'kpi-' + kpi.key"
          class="mb-4"
        >
          <v-card-title
            class="text-subtitle-1 font-weight-bold d-flex align-center kpi-header"
            @click="toggleKpi(kpi.key)"
          >
            <v-icon small class="mr-2">
              {{
                expanded_kpis[kpi.key]
                  ? "mdi-chevron-down"
                  : "mdi-chevron-right"
              }}
            </v-icon>
            <span>{{ $t(kpi.label) }}</span>
            <v-spacer></v-spacer>
            <v-tooltip bottom color="#333333">
              <template v-slot:activator="{ on }">
                <v-chip
                  small
                  :color="getPeriodMatchColor(kpi.key)"
                  dark
                  v-on="on"
                  @click.stop
                >
                  {{ getPeriodMatchLabel(kpi.key) }}
                </v-chip>
              </template>
              <div class="text-center" style="line-height: 1.6;">
                <div>
                  <strong>RMS:</strong>
                  {{ formatKpiValue(kpi, getPeriodValue(kpi.key, "veetal")) }}
                </div>
                <div>
                  <strong>PMS:</strong>
                  {{ formatKpiValue(kpi, getPeriodValue(kpi.key, "pms")) }}
                </div>
                <div>
                  <strong>{{ $t("difference") }}:</strong>
                  {{ formatKpiValue(kpi, getPeriodDiff(kpi.key)) }}
                </div>
              </div>
            </v-tooltip>
          </v-card-title>
          <v-card-text v-show="expanded_kpis[kpi.key]" class="pt-2">
            <div
              class="kpi-scroll-container"
              style="overflow-x: auto;"
              @scroll="onKpiScroll"
            >
              <table class="validation-table">
                <thead>
                  <tr>
                    <th class="source-col"></th>
                    <th
                      v-for="day in validation_days"
                      :key="'h-' + kpi.key + '-' + day"
                      class="day-col text-center"
                      :class="{ 'highlighted-day': hovered_day === day }"
                    >
                      <div class="day-header-wrapper">
                        {{ dayLabel(day) }}
                        <v-tooltip bottom v-if="dayHasMismatch(kpi.key, day)">
                          <template v-slot:activator="{ on }">
                            <v-icon
                              x-small
                              color="orange darken-2"
                              class="recalculate-icon"
                              v-on="on"
                              @click.stop="promptRecalculateDay(day)"
                              >mdi-refresh</v-icon
                            >
                          </template>
                          <span>{{ $t("recalculate_kpis") }}</span>
                        </v-tooltip>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- PMS row -->
                  <tr>
                    <td class="source-col font-weight-bold">PMS</td>
                    <td
                      v-for="day in validation_days"
                      :key="'pms-' + kpi.key + '-' + day"
                      class="day-col text-center"
                      :class="{
                        'clickable-cell': isClickableKpi(kpi.key),
                        'highlighted-day': hovered_day === day
                      }"
                      @click="onCellClick(kpi.key, day)"
                      @mouseenter="hovered_day = day"
                      @mouseleave="hovered_day = null"
                    >
                      {{
                        formatKpiValue(kpi, getDayValue(kpi.key, day, "pms"))
                      }}
                    </td>
                  </tr>
                  <!-- Veetal row -->
                  <tr>
                    <td class="source-col font-weight-bold">Veetal</td>
                    <td
                      v-for="day in validation_days"
                      :key="'veetal-' + kpi.key + '-' + day"
                      class="day-col text-center"
                      :class="{
                        'clickable-cell': isClickableKpi(kpi.key),
                        'highlighted-day': hovered_day === day
                      }"
                      @click="onCellClick(kpi.key, day)"
                      @mouseenter="hovered_day = day"
                      @mouseleave="hovered_day = null"
                    >
                      {{
                        formatKpiValue(kpi, getDayValue(kpi.key, day, "veetal"))
                      }}
                    </td>
                  </tr>
                  <!-- Match row -->
                  <tr class="match-row">
                    <td class="source-col"></td>
                    <td
                      v-for="day in validation_days"
                      :key="'match-' + kpi.key + '-' + day"
                      class="day-col text-center"
                      :class="{ 'highlighted-day': hovered_day === day }"
                      @mouseenter="hovered_day = day"
                      @mouseleave="hovered_day = null"
                    >
                      <v-chip
                        x-small
                        :color="getDayMatchColor(kpi.key, day)"
                        dark
                        class="font-weight-bold"
                      >
                        {{ getDayMatchLabel(kpi.key, day) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-card-text>
        </v-card>
        <!-- Day reservations dialog -->
        <v-dialog
          v-model="show_day_reservations"
          :fullscreen="$vuetify.breakpoint.smAndDown"
          max-width="1100"
          scrollable
        >
          <v-card v-if="!selected_reservation_id">
            <v-card-title class="d-flex align-center pb-0">
              <span>{{ $t("reservations") }} - {{ selected_day }}</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="closeDayReservations">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-tabs v-model="reservations_tab" class="px-4">
                <v-tab>
                  <v-icon small class="mr-1">mdi-database</v-icon>
                  {{ $t("rms_reservations") }}
                </v-tab>
                <v-tab v-if="selectedDayHasMismatch">
                  <v-icon small class="mr-1">mdi-compare-horizontal</v-icon>
                  {{ $t("audit") }}
                </v-tab>
              </v-tabs>
              <v-divider></v-divider>
              <v-tabs-items v-model="reservations_tab">
                <!-- RMS Reservations tab -->
                <v-tab-item class="pa-4">
                  <v-row dense class="mb-3" align="center">
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="reservation_status_filter"
                        :items="available_reservation_statuses"
                        :label="$t('filter_by_status')"
                        multiple
                        small-chips
                        deletable-chips
                        clearable
                        dense
                        outlined
                        hide-details
                        @change="onStatusFilterChange"
                      >
                        <template v-slot:selection="{ item, index }">
                          <v-chip
                            x-small
                            close
                            @click:close="removeStatusFilter(index)"
                            class="mr-1"
                          >
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-switch
                        v-model="reservation_only_rn"
                        :label="$t('only_room_nights')"
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        @change="onRnFilterChange"
                      ></v-switch>
                    </v-col>
                  </v-row>
                  <v-data-table
                    :headers="reservation_headers"
                    :items="day_reservations"
                    :server-items-length="day_reservations_total"
                    :options.sync="day_reservations_options"
                    :loading="loading_day_reservations"
                    :footer-props="{
                      'items-per-page-options': [25, 50, 100],
                      'items-per-page-text': $t('rows_per_page')
                    }"
                    item-key="_row_key"
                    dense
                    class="elevation-0"
                    :no-data-text="$t('no_data')"
                    @click:row="openReservationDetail"
                    :item-class="() => 'a-datatable-row'"
                  >
                    <template v-slot:[`item.status`]="{ item: res }">
                      <v-chip
                        x-small
                        :color="
                          res.status &&
                          (res.status === 'ok' ||
                            res.status.toLowerCase() === 'confirmed')
                            ? 'green'
                            : res.status &&
                              (res.status.toLowerCase() === 'error' ||
                                res.status.toLowerCase() === 'canceled')
                            ? 'red'
                            : 'grey'
                        "
                        dark
                      >
                        {{ res.status }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.is_rn`]="{ item: res }">
                      <v-icon
                        small
                        :color="res.is_rn ? 'green' : 'grey lighten-1'"
                      >
                        {{
                          res.is_rn ? "mdi-check-circle" : "mdi-close-circle"
                        }}
                      </v-icon>
                    </template>
                    <template v-slot:[`item.room_type`]="{ item: res }">
                      <v-tooltip
                        bottom
                        v-if="res.room_type && res.room_type.length > 10"
                      >
                        <template v-slot:activator="{ on }">
                          <span v-on="on"
                            >{{ res.room_type.substring(0, 10) }}…</span
                          >
                        </template>
                        <span>{{ res.room_type }}</span>
                      </v-tooltip>
                      <span v-else>{{ res.room_type || "-" }}</span>
                    </template>
                    <template v-slot:[`item.intermediate`]="{ item: res }">
                      <v-tooltip
                        bottom
                        v-if="res.intermediate && res.intermediate.length > 10"
                      >
                        <template v-slot:activator="{ on }">
                          <span v-on="on"
                            >{{ res.intermediate.substring(0, 10) }}…</span
                          >
                        </template>
                        <span>{{ res.intermediate }}</span>
                      </v-tooltip>
                      <span v-else>{{ res.intermediate || "-" }}</span>
                    </template>
                    <template v-slot:[`item.revenue`]="{ item: res }">
                      {{ formatCurrency(res.revenue) }}
                    </template>
                    <template v-slot:[`item.room_revenue`]="{ item: res }">
                      {{ formatCurrency(res.room_revenue) }}
                    </template>
                  </v-data-table>
                </v-tab-item>

                <!-- Audit tab -->
                <v-tab-item v-if="selectedDayHasMismatch" class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <v-switch
                      v-model="audit_only_rn"
                      :label="$t('only_room_nights')"
                      dense
                      hide-details
                      class="mt-0 pt-0"
                      @change="refetchAudit"
                    ></v-switch>
                  </div>
                  <v-progress-linear
                    v-if="loading_audit"
                    color="secondary"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>
                  <template v-else-if="audit_data">
                    <!-- Summary -->
                    <v-row class="mb-4" dense>
                      <v-col cols="6" sm="2">
                        <v-card outlined class="text-center pa-2">
                          <div class="text-caption text-uppercase">RMS</div>
                          <div class="text-h6">
                            {{ audit_data.summary.rms_count }}
                          </div>
                        </v-card>
                      </v-col>
                      <v-col cols="6" sm="2">
                        <v-card outlined class="text-center pa-2">
                          <div class="text-caption text-uppercase">PMS</div>
                          <div class="text-h6">
                            {{ audit_data.summary.pms_count }}
                          </div>
                        </v-card>
                      </v-col>
                      <v-col cols="4" sm="2">
                        <v-card
                          outlined
                          class="text-center pa-2"
                          color="green lighten-5"
                        >
                          <div class="text-caption text-uppercase">
                            {{ $t("matched") }}
                          </div>
                          <div class="text-h6 green--text">
                            {{ audit_data.summary.matched }}
                          </div>
                        </v-card>
                      </v-col>
                      <v-col cols="4" sm="2">
                        <v-card
                          outlined
                          class="text-center pa-2"
                          color="orange lighten-5"
                        >
                          <div class="text-caption text-uppercase">
                            {{ $t("only_rms") }}
                          </div>
                          <div class="text-h6 orange--text text--darken-2">
                            {{ audit_data.summary.only_rms }}
                          </div>
                        </v-card>
                      </v-col>
                      <v-col cols="4" sm="2">
                        <v-card
                          outlined
                          class="text-center pa-2"
                          color="red lighten-5"
                        >
                          <div class="text-caption text-uppercase">
                            {{ $t("only_pms") }}
                          </div>
                          <div class="text-h6 red--text">
                            {{ audit_data.summary.only_pms }}
                          </div>
                        </v-card>
                      </v-col>
                      <v-col cols="4" sm="2">
                        <v-card
                          outlined
                          class="text-center pa-2"
                          color="purple lighten-5"
                        >
                          <div class="text-caption text-uppercase">
                            {{ $t("status_mismatch") }}
                          </div>
                          <div class="text-h6 purple--text">
                            {{ audit_data.summary.status_mismatch }}
                          </div>
                        </v-card>
                      </v-col>
                    </v-row>

                    <!-- Reimport all button -->
                    <div
                      v-if="
                        (audit_data.only_rms && audit_data.only_rms.length) ||
                          (audit_data.only_pms && audit_data.only_pms.length)
                      "
                      class="d-flex justify-end mb-3"
                    >
                      <v-btn
                        small
                        color="secondary"
                        depressed
                        :loading="reimporting_all"
                        :disabled="reimporting_all || !!reimporting_pms_id"
                        @click="reimportAllReservations"
                      >
                        <v-icon small class="mr-1">mdi-refresh</v-icon>
                        {{ $t("reimport_all") }}
                      </v-btn>
                    </div>

                    <!-- Only in RMS -->
                    <template
                      v-if="audit_data.only_rms && audit_data.only_rms.length"
                    >
                      <div
                        class="text-subtitle-2 font-weight-bold orange--text text--darken-2 mb-1"
                      >
                        <v-icon small color="orange darken-2" class="mr-1"
                          >mdi-arrow-right-bold</v-icon
                        >
                        {{ $t("only_rms") }} ({{ audit_data.only_rms.length }})
                      </div>
                      <v-data-table
                        :headers="audit_rms_headers"
                        :items="audit_data.only_rms"
                        dense
                        class="elevation-0 mb-4"
                        :no-data-text="$t('no_data')"
                        hide-default-footer
                        :items-per-page="-1"
                      >
                        <template v-slot:[`item.status`]="{ item: res }">
                          <v-chip x-small dark color="orange darken-1">{{
                            res.status
                          }}</v-chip>
                        </template>
                        <template v-slot:[`item.actions`]="{ item: res }">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                x-small
                                v-on="on"
                                :loading="reimporting_pms_id === res.pms_id"
                                :disabled="
                                  !!reimporting_pms_id || reimporting_all
                                "
                                @click.stop="reimportReservation(res.pms_id)"
                              >
                                <v-icon small>mdi-refresh</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("reimport") }}</span>
                          </v-tooltip>
                        </template>
                      </v-data-table>
                    </template>

                    <!-- Only in PMS -->
                    <template
                      v-if="audit_data.only_pms && audit_data.only_pms.length"
                    >
                      <div
                        class="text-subtitle-2 font-weight-bold red--text mb-1"
                      >
                        <v-icon small color="red" class="mr-1"
                          >mdi-arrow-left-bold</v-icon
                        >
                        {{ $t("only_pms") }} ({{ audit_data.only_pms.length }})
                      </div>
                      <v-data-table
                        :headers="audit_pms_headers"
                        :items="audit_data.only_pms"
                        dense
                        class="elevation-0 mb-4"
                        :no-data-text="$t('no_data')"
                        hide-default-footer
                        :items-per-page="-1"
                      >
                        <template v-slot:[`item.status`]="{ item: res }">
                          <v-chip x-small dark color="red">{{
                            res.status
                          }}</v-chip>
                        </template>
                        <template v-slot:[`item.actions`]="{ item: res }">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                icon
                                x-small
                                v-on="on"
                                :loading="reimporting_pms_id === res.pms_id"
                                :disabled="
                                  !!reimporting_pms_id || reimporting_all
                                "
                                @click.stop="reimportReservation(res.pms_id)"
                              >
                                <v-icon small>mdi-refresh</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("reimport") }}</span>
                          </v-tooltip>
                        </template>
                      </v-data-table>
                    </template>

                    <!-- Status mismatch -->
                    <template
                      v-if="
                        audit_data.status_mismatch &&
                          audit_data.status_mismatch.length
                      "
                    >
                      <div
                        class="text-subtitle-2 font-weight-bold purple--text mb-1"
                      >
                        <v-icon small color="purple" class="mr-1"
                          >mdi-swap-horizontal</v-icon
                        >
                        {{ $t("status_mismatch") }} ({{
                          audit_data.status_mismatch.length
                        }})
                      </div>
                      <v-data-table
                        :headers="audit_mismatch_headers"
                        :items="audit_data.status_mismatch"
                        dense
                        class="elevation-0 mb-4"
                        :no-data-text="$t('no_data')"
                        hide-default-footer
                        :items-per-page="-1"
                      >
                        <template v-slot:[`item.rms_status`]="{ item: res }">
                          <v-chip x-small dark color="blue">{{
                            res.rms_status
                          }}</v-chip>
                        </template>
                        <template v-slot:[`item.pms_status`]="{ item: res }">
                          <v-chip x-small dark color="deep-orange">{{
                            res.pms_status
                          }}</v-chip>
                        </template>
                      </v-data-table>
                    </template>

                    <!-- Matched -->
                    <template
                      v-if="
                        audit_data.matched_list &&
                          audit_data.matched_list.length
                      "
                    >
                      <div
                        class="text-subtitle-2 font-weight-bold green--text mb-1"
                      >
                        <v-icon small color="green" class="mr-1"
                          >mdi-check-bold</v-icon
                        >
                        {{ $t("matched") }} ({{
                          audit_data.matched_list.length
                        }})
                      </div>
                      <v-data-table
                        :headers="audit_matched_headers"
                        :items="audit_data.matched_list"
                        dense
                        class="elevation-0 mb-4"
                        :no-data-text="$t('no_data')"
                        hide-default-footer
                        :items-per-page="-1"
                      >
                        <template v-slot:[`item.status`]="{ item: res }">
                          <v-chip x-small dark color="green">{{
                            res.status
                          }}</v-chip>
                        </template>
                      </v-data-table>
                    </template>
                  </template>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
          <v-card v-else>
            <reservation-detail
              :reservationId="selected_reservation_id"
              @close="selected_reservation_id = null"
              @close-dialog="closeDayReservations"
            />
          </v-card>
        </v-dialog>

        <!-- Availability validation dialog -->
        <v-dialog
          v-model="show_availability_validation"
          :fullscreen="$vuetify.breakpoint.smAndDown"
          max-width="1400"
          scrollable
        >
          <v-card>
            <v-card-title class="d-flex align-center">
              <span
                >{{ $t("availability_validation") }} -
                {{ selected_availability_day }}</span
              >
              <v-spacer></v-spacer>
              <v-btn icon @click="closeAvailabilityValidation">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-progress-linear
                v-if="loading_availability"
                color="secondary"
                indeterminate
                class="mb-4"
              ></v-progress-linear>
              <div v-else-if="availability_data" style="overflow-x: auto;">
                <table class="availability-table">
                  <thead>
                    <tr>
                      <th rowspan="2" class="room-type-col">
                        {{ $t("room_type") }}
                      </th>
                      <th
                        :colspan="availability_rms_fields.length"
                        class="group-header rms-group"
                      >
                        RMS
                      </th>
                      <th
                        :colspan="availability_pms_fields.length"
                        class="group-header pms-group"
                      >
                        PMS
                      </th>
                    </tr>
                    <tr>
                      <th
                        v-for="f in availability_rms_fields"
                        :key="'rms-h-' + f.key"
                        class="data-col rms-col"
                      >
                        {{ f.label }}
                      </th>
                      <th
                        v-for="f in availability_pms_fields"
                        :key="'pms-h-' + f.key"
                        class="data-col pms-col"
                      >
                        {{ f.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="rt in availability_data.room_types"
                      :key="rt.code"
                    >
                      <td class="room-type-col">
                        <span class="font-weight-bold">{{ rt.code }}</span>
                        <span class="text--secondary ml-1">{{ rt.name }}</span>
                      </td>
                      <td
                        v-for="f in availability_rms_fields"
                        :key="'rms-' + rt.code + '-' + f.key"
                        class="data-col text-center"
                      >
                        {{ rt.rms ? rt.rms[f.key] : "-" }}
                      </td>
                      <td
                        v-for="f in availability_pms_fields"
                        :key="'pms-' + rt.code + '-' + f.key"
                        class="data-col text-center"
                      >
                        {{ rt.pms ? rt.pms[f.key] : "-" }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="availability_data.totals">
                    <tr class="totals-row">
                      <td class="room-type-col font-weight-bold">TOTAL</td>
                      <td
                        v-for="f in availability_rms_fields"
                        :key="'rms-total-' + f.key"
                        class="data-col text-center font-weight-bold"
                      >
                        {{
                          availability_data.totals.rms
                            ? availability_data.totals.rms[f.key]
                            : "-"
                        }}
                      </td>
                      <td
                        v-for="f in availability_pms_fields"
                        :key="'pms-total-' + f.key"
                        class="data-col text-center font-weight-bold"
                      >
                        {{
                          availability_data.totals.pms
                            ? availability_data.totals.pms[f.key]
                            : "-"
                        }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Revenue validation dialog -->
        <v-dialog
          v-model="show_revenue_validation"
          :fullscreen="$vuetify.breakpoint.smAndDown"
          max-width="1200"
          scrollable
        >
          <v-card v-if="!selected_reservation_id">
            <v-card-title class="d-flex align-center pb-0">
              <span
                >{{ $t("revenue_validation") }} -
                {{ selected_revenue_day }}</span
              >
              <v-spacer></v-spacer>
              <v-btn icon @click="closeRevenueValidation">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <div class="pt-2 pb-0 px-4">
              <v-row dense>
                <v-col
                  v-for="kpi in revenue_kpis"
                  :key="'rev-summary-' + kpi"
                  cols="12"
                  sm="4"
                >
                  <v-card outlined class="pa-3">
                    <div
                      class="text-caption font-weight-bold text-uppercase text-center mb-2"
                    >
                      {{ $t(kpi) }}
                    </div>
                    <div class="d-flex justify-space-around align-center">
                      <div class="text-center">
                        <div class="text-caption text--secondary">PMS</div>
                        <div class="text-h6">
                          {{
                            formatKpiValue(
                              getKpiDef(kpi),
                              getDayValue(kpi, selected_revenue_day, "pms")
                            )
                          }}
                        </div>
                      </div>
                      <div class="text-center">
                        <div class="text-caption text--secondary">Veetal</div>
                        <div class="text-h6">
                          {{
                            formatKpiValue(
                              getKpiDef(kpi),
                              getDayValue(kpi, selected_revenue_day, "veetal")
                            )
                          }}
                        </div>
                      </div>
                      <div class="text-center">
                        <v-chip
                          small
                          :color="getDayMatchColor(kpi, selected_revenue_day)"
                          dark
                          class="font-weight-bold"
                        >
                          {{ getDayMatchLabel(kpi, selected_revenue_day) }}
                        </v-chip>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
            <v-card-text class="pa-0">
              <v-tabs v-model="revenue_tab" class="px-4">
                <v-tab href="#rev-reservations">
                  <v-icon small class="mr-1">mdi-database</v-icon>
                  {{ $t("rms_reservations") }}
                </v-tab>
                <v-tab v-if="selectedRevenueDayHasMismatch" href="#rev-audit">
                  <v-icon small class="mr-1">mdi-compare-horizontal</v-icon>
                  {{ $t("audit") }}
                </v-tab>
                <v-tab href="#rev-charges">
                  <v-icon small class="mr-1">mdi-receipt-text</v-icon>
                  {{ $t("rms_charges") }}
                </v-tab>
              </v-tabs>
              <v-divider></v-divider>
              <v-tabs-items v-model="revenue_tab">
                <!-- Revenue RMS reservations tab -->
                <v-tab-item value="rev-reservations" class="pa-4">
                  <v-progress-linear
                    v-if="loading_revenue"
                    color="secondary"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>
                  <template v-else>
                    <v-data-table
                      :headers="revenue_headers"
                      :items="revenue_reservations"
                      item-key="_row_key"
                      dense
                      class="elevation-0"
                      :no-data-text="$t('no_data')"
                      hide-default-footer
                      :items-per-page="-1"
                      @click:row="openReservationDetail"
                      :item-class="() => 'a-datatable-row'"
                    >
                      <template v-slot:[`item.status`]="{ item: res }">
                        <v-chip
                          x-small
                          :color="
                            res.status &&
                            (res.status === 'ok' ||
                              res.status.toLowerCase() === 'confirmed')
                              ? 'green'
                              : res.status &&
                                (res.status.toLowerCase() === 'error' ||
                                  res.status.toLowerCase() === 'canceled')
                              ? 'red'
                              : 'grey'
                          "
                          dark
                        >
                          {{ res.status }}
                        </v-chip>
                      </template>
                      <template v-slot:[`item.revenue`]="{ item: res }">
                        {{ formatCurrency(res.revenue) }}
                      </template>
                      <template v-slot:[`item.room_revenue`]="{ item: res }">
                        {{ formatCurrency(res.room_revenue) }}
                      </template>
                      <template v-slot:body.append>
                        <tr
                          class="font-weight-bold totals-row"
                          v-if="revenue_reservations.length"
                        >
                          <td
                            :colspan="revenue_headers.length - 2"
                            class="text-right"
                          >
                            TOTAL
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(revenue_totals.revenue) }}
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(revenue_totals.room_revenue) }}
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </template>
                </v-tab-item>

                <!-- Revenue audit tab -->
                <v-tab-item
                  v-if="selectedRevenueDayHasMismatch"
                  value="rev-audit"
                  class="pa-4"
                >
                  <v-progress-linear
                    v-if="loading_revenue_audit"
                    color="secondary"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>
                  <v-alert
                    v-else-if="revenue_audit_error"
                    type="error"
                    outlined
                    class="mb-4"
                  >
                    {{ revenue_audit_error }}
                  </v-alert>
                  <template v-else-if="revenue_audit_data">
                    <div class="d-flex align-center mb-3">
                      <v-chip
                        small
                        outlined
                        class="mr-2"
                        color="orange darken-2"
                      >
                        <v-icon x-small left>mdi-alert</v-icon>
                        {{ revenueAuditDiscrepancyCount }}
                        {{ $t("discrepancies") }}
                      </v-chip>
                      <v-btn
                        x-small
                        text
                        color="secondary"
                        @click="expandAllAuditRows"
                      >
                        <v-icon x-small class="mr-1">mdi-expand-all</v-icon>
                        {{ $t("expand_all") }}
                      </v-btn>
                      <v-btn x-small text @click="collapseAllAuditRows">
                        <v-icon x-small class="mr-1">mdi-collapse-all</v-icon>
                        {{ $t("collapse_all") }}
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn
                        v-if="revenueAuditDiscrepancyCount > 0"
                        small
                        color="secondary"
                        depressed
                        :loading="reimporting_all_revenue"
                        :disabled="
                          reimporting_all_revenue || !!reimporting_pms_id
                        "
                        @click="reimportAllRevenueAuditReservations"
                      >
                        <v-icon small class="mr-1">mdi-refresh</v-icon>
                        {{ $t("reimport_all") }}
                      </v-btn>
                    </div>
                    <div style="overflow-x: auto;">
                      <table class="revenue-audit-table">
                        <thead>
                          <tr>
                            <th rowspan="2" class="room-type-col"></th>
                            <th rowspan="2" class="room-type-col">
                              {{ $t("room_type") }}
                            </th>
                            <th :colspan="3" class="group-header rms-group">
                              RMS
                            </th>
                            <th :colspan="3" class="group-header pms-group">
                              PMS
                            </th>
                          </tr>
                          <tr>
                            <th class="data-col rms-col">Room Rev.</th>
                            <th class="data-col rms-col">
                              {{ $t("reservations") }}
                            </th>
                            <th class="data-col rms-col">ADR</th>
                            <th class="data-col pms-col">Room Rev.</th>
                            <th class="data-col pms-col">
                              {{ $t("reservations") }}
                            </th>
                            <th class="data-col pms-col">ADR</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="rt in revenue_audit_data.room_types">
                            <tr
                              :key="'rt-' + rt.code"
                              class="audit-room-type-row"
                              :class="{
                                'audit-discrepancy-row': rt.has_discrepancy,
                                'audit-expanded': expanded_audit_rows[rt.code]
                              }"
                              @click="toggleAuditRow(rt.code)"
                            >
                              <td class="expand-col text-center">
                                <v-icon
                                  small
                                  v-if="
                                    rt.reservations && rt.reservations.length
                                  "
                                >
                                  {{
                                    expanded_audit_rows[rt.code]
                                      ? "mdi-chevron-down"
                                      : "mdi-chevron-right"
                                  }}
                                </v-icon>
                              </td>
                              <td class="room-type-col">
                                <span class="font-weight-bold">{{
                                  rt.code
                                }}</span>
                                <span class="text--secondary ml-1">{{
                                  rt.name
                                }}</span>
                                <v-icon
                                  v-if="rt.has_discrepancy"
                                  x-small
                                  color="orange darken-2"
                                  class="ml-1"
                                  >mdi-alert-circle</v-icon
                                >
                              </td>
                              <td class="data-col text-right">
                                {{
                                  formatCurrency(
                                    rt.rms ? rt.rms.room_revenue : null
                                  )
                                }}
                              </td>
                              <td class="data-col text-center">
                                {{ rt.rms ? rt.rms.reservations_count : 0 }}
                              </td>
                              <td class="data-col text-right">
                                {{ formatCurrency(rt.rms ? rt.rms.adr : null) }}
                              </td>
                              <td class="data-col text-right">
                                {{
                                  formatCurrency(
                                    rt.pms ? rt.pms.room_revenue : null
                                  )
                                }}
                              </td>
                              <td class="data-col text-center">
                                {{ rt.pms ? rt.pms.reservations_count : 0 }}
                              </td>
                              <td class="data-col text-right">
                                {{ formatCurrency(rt.pms ? rt.pms.adr : null) }}
                              </td>
                            </tr>
                            <tr
                              v-if="
                                expanded_audit_rows[rt.code] &&
                                  rt.reservations &&
                                  rt.reservations.length
                              "
                              :key="'rt-detail-' + rt.code"
                              class="audit-detail-row"
                            >
                              <td :colspan="9" class="pa-0">
                                <table class="audit-reservations-table">
                                  <thead>
                                    <tr>
                                      <th class="text-left">PMS ID</th>
                                      <th class="text-left">ID</th>
                                      <th class="text-left">
                                        {{ $t("guest") }}
                                      </th>
                                      <th class="text-left">
                                        {{ $t("room") }}
                                      </th>
                                      <th class="text-left">Check-in</th>
                                      <th class="text-left">Check-out</th>
                                      <th class="text-left">
                                        {{ $t("status") }}
                                      </th>
                                      <th class="text-right">RMS Rev.</th>
                                      <th class="text-right">PMS Rev.</th>
                                      <th class="text-right">
                                        {{ $t("difference") }}
                                      </th>
                                      <th class="text-center"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr
                                      v-for="(res, ri) in rt.reservations"
                                      :key="'res-' + rt.code + '-' + ri"
                                      :class="{
                                        'audit-res-discrepancy':
                                          res.has_discrepancy,
                                        'audit-res-only-rms':
                                          res.discrepancy_type === 'only_rms',
                                        'audit-res-only-pms':
                                          res.discrepancy_type === 'only_pms',
                                        'audit-res-mismatch':
                                          res.discrepancy_type ===
                                          'revenue_mismatch'
                                      }"
                                    >
                                      <td>{{ res.pms_id || "-" }}</td>
                                      <td>
                                        <a
                                          v-if="res.id"
                                          href="#"
                                          class="text-decoration-none"
                                          @click.prevent="
                                            openReservationDetail(res)
                                          "
                                          >{{ res.id }}</a
                                        >
                                        <span v-else>-</span>
                                      </td>
                                      <td>{{ res.guest_name || "-" }}</td>
                                      <td>{{ res.room || "-" }}</td>
                                      <td>{{ res.check_in || "-" }}</td>
                                      <td>{{ res.check_out || "-" }}</td>
                                      <td>
                                        <v-chip
                                          x-small
                                          :color="
                                            res.status === 'confirmed'
                                              ? 'green'
                                              : res.status === 'canceled'
                                              ? 'red'
                                              : 'grey'
                                          "
                                          dark
                                          >{{ res.status || "-" }}</v-chip
                                        >
                                      </td>
                                      <td class="text-right">
                                        {{
                                          formatCurrency(res.rms_room_revenue)
                                        }}
                                      </td>
                                      <td class="text-right">
                                        {{
                                          formatCurrency(res.pms_room_revenue)
                                        }}
                                      </td>
                                      <td class="text-right">
                                        <span
                                          v-if="res.has_discrepancy"
                                          :class="{
                                            'orange--text text--darken-2':
                                              res.discrepancy_type ===
                                              'only_rms',
                                            'red--text':
                                              res.discrepancy_type ===
                                              'only_pms',
                                            'deep-orange--text':
                                              res.discrepancy_type ===
                                              'revenue_mismatch'
                                          }"
                                          class="font-weight-bold"
                                        >
                                          {{
                                            formatRevenueDiff(res.revenue_diff)
                                          }}
                                        </span>
                                        <span v-else class="green--text"
                                          >-</span
                                        >
                                      </td>
                                      <td
                                        class="text-center"
                                        style="white-space: nowrap;"
                                      >
                                        <v-tooltip
                                          bottom
                                          v-if="res.has_discrepancy"
                                        >
                                          <template v-slot:activator="{ on }">
                                            <v-icon
                                              x-small
                                              v-on="on"
                                              :color="
                                                discrepancyIconColor(
                                                  res.discrepancy_type
                                                )
                                              "
                                              >{{
                                                discrepancyIcon(
                                                  res.discrepancy_type
                                                )
                                              }}</v-icon
                                            >
                                          </template>
                                          <span>{{
                                            discrepancyLabel(
                                              res.discrepancy_type
                                            )
                                          }}</span>
                                        </v-tooltip>
                                        <v-icon v-else x-small color="green"
                                          >mdi-check-circle</v-icon
                                        >
                                        <v-tooltip
                                          bottom
                                          v-if="
                                            res.has_discrepancy && res.pms_id
                                          "
                                        >
                                          <template v-slot:activator="{ on }">
                                            <v-btn
                                              icon
                                              x-small
                                              v-on="on"
                                              class="ml-1"
                                              :loading="
                                                reimporting_pms_id ===
                                                  res.pms_id
                                              "
                                              :disabled="
                                                !!reimporting_pms_id ||
                                                  reimporting_all_revenue
                                              "
                                              @click.stop="
                                                reimportReservation(res.pms_id)
                                              "
                                            >
                                              <v-icon small>mdi-refresh</v-icon>
                                            </v-btn>
                                          </template>
                                          <span>{{ $t("reimport") }}</span>
                                        </v-tooltip>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                        <tfoot v-if="revenue_audit_data.totals">
                          <tr class="totals-row">
                            <td class="expand-col"></td>
                            <td class="room-type-col font-weight-bold">
                              TOTAL
                            </td>
                            <td class="data-col text-right font-weight-bold">
                              {{
                                formatCurrency(
                                  revenue_audit_data.totals.rms
                                    ? revenue_audit_data.totals.rms.room_revenue
                                    : null
                                )
                              }}
                            </td>
                            <td class="data-col text-center font-weight-bold">
                              {{
                                revenue_audit_data.totals.rms
                                  ? revenue_audit_data.totals.rms
                                      .reservations_count
                                  : 0
                              }}
                            </td>
                            <td class="data-col text-right font-weight-bold">
                              {{
                                formatCurrency(
                                  revenue_audit_data.totals.rms
                                    ? revenue_audit_data.totals.rms.adr
                                    : null
                                )
                              }}
                            </td>
                            <td class="data-col text-right font-weight-bold">
                              {{
                                formatCurrency(
                                  revenue_audit_data.totals.pms
                                    ? revenue_audit_data.totals.pms.room_revenue
                                    : null
                                )
                              }}
                            </td>
                            <td class="data-col text-center font-weight-bold">
                              {{
                                revenue_audit_data.totals.pms
                                  ? revenue_audit_data.totals.pms
                                      .reservations_count
                                  : 0
                              }}
                            </td>
                            <td class="data-col text-right font-weight-bold">
                              {{
                                formatCurrency(
                                  revenue_audit_data.totals.pms
                                    ? revenue_audit_data.totals.pms.adr
                                    : null
                                )
                              }}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </template>
                </v-tab-item>

                <!-- RMS Charges tab -->
                <v-tab-item value="rev-charges" class="pa-4">
                  <v-progress-linear
                    v-if="loading_revenue_charges"
                    color="secondary"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>
                  <template v-else>
                    <v-row dense class="mb-3" align="center">
                      <v-col cols="12" sm="4">
                        <v-select
                          v-model="charges_service_type_filter"
                          :items="available_charges_service_types"
                          :label="$t('service_type')"
                          multiple
                          small-chips
                          deletable-chips
                          clearable
                          dense
                          outlined
                          hide-details
                        ></v-select>
                      </v-col>
                      <v-col cols="6" sm="3">
                        <v-select
                          v-model="charges_in_production_filter"
                          :items="boolean_filter_options"
                          :label="$t('in_production')"
                          clearable
                          dense
                          outlined
                          hide-details
                        ></v-select>
                      </v-col>
                      <v-col cols="6" sm="3">
                        <v-select
                          v-model="charges_in_adr_filter"
                          :items="boolean_filter_options"
                          :label="$t('in_adr')"
                          clearable
                          dense
                          outlined
                          hide-details
                        ></v-select>
                      </v-col>
                    </v-row>
                    <v-data-table
                      :headers="revenue_charges_headers"
                      :items="filtered_revenue_charges"
                      item-key="_row_key"
                      dense
                      class="elevation-0"
                      :no-data-text="$t('no_data')"
                      hide-default-footer
                      :items-per-page="-1"
                    >
                      <template
                        v-slot:[`item.in_production`]="{ item: charge }"
                      >
                        <v-icon
                          small
                          :color="
                            charge.in_production ? 'green' : 'grey lighten-1'
                          "
                        >
                          {{
                            charge.in_production
                              ? "mdi-check-circle"
                              : "mdi-close-circle"
                          }}
                        </v-icon>
                      </template>
                      <template v-slot:[`item.in_adr`]="{ item: charge }">
                        <v-icon
                          small
                          :color="charge.in_adr ? 'green' : 'grey lighten-1'"
                        >
                          {{
                            charge.in_adr
                              ? "mdi-check-circle"
                              : "mdi-close-circle"
                          }}
                        </v-icon>
                      </template>
                      <template v-slot:[`item.revenue`]="{ item: charge }">
                        {{ formatCurrency(charge.revenue) }}
                      </template>
                      <template v-slot:body.append>
                        <tr
                          class="font-weight-bold totals-row"
                          v-if="filtered_revenue_charges.length"
                        >
                          <td
                            :colspan="revenue_charges_headers.length - 1"
                            class="text-right"
                          >
                            TOTAL
                          </td>
                          <td class="text-right">
                            {{ formatCurrency(revenue_charges_total) }}
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </template>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
          <v-card v-else>
            <reservation-detail
              :reservationId="selected_reservation_id"
              @close="selected_reservation_id = null"
              @close-dialog="closeRevenueValidation"
            />
          </v-card>
        </v-dialog>

        <!-- Recalculate KPIs confirmation dialog -->
        <v-dialog v-model="show_recalculate_confirm" max-width="420" persistent>
          <v-card>
            <v-card-title class="text-subtitle-1">
              <v-icon color="orange darken-2" class="mr-2">mdi-refresh</v-icon>
              {{ $t("recalculate_kpis") }}
            </v-card-title>
            <v-card-text>
              {{ $t("recalculate_kpis_confirm", { day: recalculate_day }) }}
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                text
                @click="cancelRecalculateDay"
                :disabled="!!recalculating_day"
              >
                {{ $t("cancel") }}
              </v-btn>
              <v-btn
                color="orange darken-2"
                dark
                depressed
                :loading="!!recalculating_day"
                @click="confirmRecalculateDay"
              >
                {{ $t("recalculate") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </template>
    <template v-slot:loading>
      <v-card class="mt-2">
        <v-card-text>
          <v-skeleton-loader
            type="card-heading, table"
            class="mx-auto"
          ></v-skeleton-loader>
        </v-card-text>
      </v-card>
    </template>
  </a-base-component>
</template>

<script>
import { mapGetters } from "vuex";
import ABaseComponent from "@/components/aBaseComponent.vue";
import ReservationDetail from "@/components/reservations/ReservationDetail.vue";

export default {
  components: { ABaseComponent, ReservationDetail },
  data: () => ({
    subscribed_modules: [],
    start_date: null,
    end_date: null,
    menu_start: false,
    menu_end: false,
    validation_source: "forecast",
    loading: false,
    validation_data: null,
    error_message: null,
    show_day_reservations: false,
    selected_day: null,
    day_reservations: [],
    day_reservations_total: 0,
    loading_day_reservations: false,
    selected_reservation_id: null,
    day_reservations_options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      sortDesc: []
    },
    reservations_tab: 0,
    audit_data: null,
    loading_audit: false,
    audit_only_rn: true,
    reservation_status_filter: [],
    reservation_only_rn: false,
    all_reservation_statuses: [],
    reimporting_pms_id: null,
    reimporting_all: false,
    reimporting_all_revenue: false,
    reservations_kpis: [
      "rooms_sold",
      "rooms_occupied",
      "occupancy",
      "complimentary",
      "no_show",
      "tentative"
    ],
    revenue_kpis: ["room_revenue", "adr", "revpar"],
    show_revenue_validation: false,
    selected_revenue_day: null,
    revenue_reservations: [],
    loading_revenue: false,
    revenue_tab: "rev-reservations",
    revenue_audit_data: null,
    revenue_audit_error: null,
    loading_revenue_audit: false,
    revenue_charges: [],
    loading_revenue_charges: false,
    charges_service_type_filter: [],
    charges_in_production_filter: null,
    charges_in_adr_filter: null,
    show_availability_validation: false,
    availability_data: null,
    loading_availability: false,
    selected_availability_day: null,
    availability_kpis: ["total_rooms", "available_rooms", "out_of_order"],
    show_recalculate_confirm: false,
    recalculate_day: null,
    recalculating_day: null,
    availability_rms_fields: [
      { key: "open", label: "Open" },
      { key: "out_of_order_1", label: "OOO 1" },
      { key: "out_of_order_2", label: "OOO 2" },
      { key: "out_of_service", label: "OOS" },
      { key: "out_of_inventory", label: "OOI" },
      { key: "overbooking", label: "Overbooking" },
      { key: "allotment", label: "Allotment" }
    ],
    availability_pms_fields: [
      { key: "physicalRoom", label: "Physical" },
      // { key: "nonPhysicalRoom", label: "Non-Physical" },
      { key: "reserved", label: "Reserved" },
      { key: "available", label: "Available" },
      { key: "outOfOrder", label: "OOO" },
      { key: "outOfService", label: "OOS" },
      { key: "overbooking", label: "Overbooking" },
      { key: "noShow", label: "No Show" },
      { key: "cancelled", label: "Cancelled" },
      { key: "tentative", label: "Tentative" },
      { key: "reservedHouseUse", label: "House Use" },
      { key: "onHold", label: "On Hold" },
      { key: "reservedDayUse", label: "Day Use" },
      // { key: "arrival", label: "Arrivals" },
      // { key: "departure", label: "Departures" },
      // { key: "earlyDeparture", label: "Early Dep." },
      { key: "roomBlock", label: "Room Block" },
      { key: "roomBlockConsumed", label: "Block Cons." },
      { key: "roomBlockAvailable", label: "Block Avail." }
    ],
    expanded_kpis: {},
    expanded_audit_rows: {},
    hovered_day: null,
    syncing_scroll: false,
    kpi_definitions: [
      { key: "total_rooms", label: "total_rooms", type: "integer" },
      { key: "available_rooms", label: "available_rooms", type: "integer" },
      { key: "out_of_order", label: "out_of_order", type: "integer" },
      { key: "rooms_sold", label: "rooms_sold", type: "integer" },
      { key: "rooms_occupied", label: "rooms_occupied", type: "integer" },
      { key: "occupancy", label: "occupancy", type: "percent" },
      { key: "complimentary", label: "complimentary", type: "integer" },
      // { key: "cancellations", label: "cancellations", type: "integer" },
      { key: "no_show", label: "no_show", type: "integer" },
      { key: "tentative", label: "tentative", type: "integer" },
      { key: "room_revenue", label: "room_revenue", type: "currency" },
      { key: "adr", label: "adr", type: "currency" },
      { key: "revpar", label: "revpar", type: "currency" }
    ]
  }),
  computed: {
    isUlysesPms() {
      return this.current_hotel && this.current_hotel.pms === "ulyses";
    },
    validation_source_options() {
      return [
        { text: "Forecast", value: "forecast" },
        { text: "Pickup", value: "pickup" }
      ];
    },
    validation_days() {
      if (!this.validation_data || !this.validation_data.days) return [];
      return Object.keys(this.validation_data.days).sort();
    },
    reservation_headers() {
      return [
        { text: "ID", align: "left", sortable: true, value: "id" },
        { text: "PMS ID", align: "left", sortable: true, value: "pms_id" },
        {
          text: this.$t("status"),
          align: "left",
          sortable: true,
          value: "status"
        },
        { text: "RN", align: "center", sortable: true, value: "is_rn" },
        {
          text: this.$t("room_type"),
          align: "left",
          sortable: true,
          value: "room_type"
        },
        { text: this.$t("room"), align: "left", sortable: true, value: "room" },
        { text: "Check-in", align: "left", sortable: true, value: "check_in" },
        {
          text: "Check-out",
          align: "left",
          sortable: true,
          value: "check_out"
        },
        { text: "Pax", align: "left", sortable: true, value: "pax" },
        {
          text: this.$t("intermediary"),
          align: "left",
          sortable: true,
          value: "intermediate"
        },
        { text: "Revenue", align: "right", sortable: true, value: "revenue" },
        {
          text: "Room Revenue",
          align: "right",
          sortable: true,
          value: "room_revenue"
        }
      ];
    },
    available_reservation_statuses() {
      return [...this.all_reservation_statuses].sort();
    },
    selectedDayHasMismatch() {
      if (!this.selected_day) return false;
      const match = this.getDayMatch("rooms_sold", this.selected_day);
      return match !== null && match !== undefined && match < 99.9;
    },
    selectedRevenueDayHasMismatch() {
      if (!this.selected_revenue_day) return false;
      for (const kpi of this.revenue_kpis) {
        const match = this.getDayMatch(kpi, this.selected_revenue_day);
        if (match !== null && match !== undefined && match < 99.9) return true;
      }
      return false;
    },
    revenue_headers() {
      return [
        { text: "ID", align: "left", sortable: true, value: "id" },
        { text: "PMS ID", align: "left", sortable: true, value: "pms_id" },
        {
          text: this.$t("status"),
          align: "left",
          sortable: true,
          value: "status"
        },
        {
          text: this.$t("room_type"),
          align: "left",
          sortable: true,
          value: "room_type"
        },
        { text: this.$t("room"), align: "left", sortable: true, value: "room" },
        { text: "Check-in", align: "left", sortable: true, value: "check_in" },
        {
          text: "Check-out",
          align: "left",
          sortable: true,
          value: "check_out"
        },
        { text: "Pax", align: "center", sortable: true, value: "pax" },
        { text: "Revenue", align: "right", sortable: true, value: "revenue" },
        {
          text: "Room Revenue",
          align: "right",
          sortable: true,
          value: "room_revenue"
        }
      ];
    },
    revenue_charges_headers() {
      return [
        {
          text: this.$t("service"),
          align: "left",
          sortable: true,
          value: "service"
        },
        {
          text: this.$t("service_type"),
          align: "left",
          sortable: true,
          value: "service_type"
        },
        {
          text: this.$t("in_production"),
          align: "center",
          sortable: true,
          value: "in_production"
        },
        {
          text: this.$t("in_adr"),
          align: "center",
          sortable: true,
          value: "in_adr"
        },
        {
          text: this.$t("charges_count"),
          align: "center",
          sortable: true,
          value: "count"
        },
        { text: "Revenue", align: "right", sortable: true, value: "revenue" }
      ];
    },
    filtered_revenue_charges() {
      if (!this.revenue_charges || !this.revenue_charges.length) return [];
      return this.revenue_charges.filter(c => {
        if (
          this.charges_service_type_filter.length &&
          !this.charges_service_type_filter.includes(c.service_type)
        ) {
          return false;
        }
        if (
          this.charges_in_production_filter != null &&
          c.in_production !== this.charges_in_production_filter
        ) {
          return false;
        }
        if (
          this.charges_in_adr_filter != null &&
          c.in_adr !== this.charges_in_adr_filter
        ) {
          return false;
        }
        return true;
      });
    },
    revenue_charges_total() {
      return this.filtered_revenue_charges.reduce(
        (acc, c) => acc + (parseFloat(c.revenue) || 0),
        0
      );
    },
    available_charges_service_types() {
      if (!this.revenue_charges || !this.revenue_charges.length) return [];
      const types = new Set(
        this.revenue_charges.map(c => c.service_type).filter(Boolean)
      );
      return [...types].sort();
    },
    boolean_filter_options() {
      return [
        { text: this.$t("yes"), value: true },
        { text: this.$t("no"), value: false }
      ];
    },
    revenue_totals() {
      if (!this.revenue_reservations || !this.revenue_reservations.length) {
        return { revenue: 0, room_revenue: 0 };
      }
      return this.revenue_reservations.reduce(
        (acc, r) => {
          acc.revenue += parseFloat(r.revenue) || 0;
          acc.room_revenue += parseFloat(r.room_revenue) || 0;
          return acc;
        },
        { revenue: 0, room_revenue: 0 }
      );
    },
    audit_rms_headers() {
      return [
        { text: "ID", value: "id", sortable: true },
        { text: "PMS ID", value: "pms_id", sortable: true },
        { text: this.$t("status"), value: "status", sortable: true },
        { text: "Key", value: "key", sortable: true },
        { text: this.$t("room_type"), value: "room_type", sortable: true },
        { text: "Check-in", value: "check_in", sortable: true },
        { text: "Check-out", value: "check_out", sortable: true },
        { text: "Pax", value: "pax", sortable: true },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "center",
          width: "50px"
        }
      ];
    },
    audit_pms_headers() {
      return [
        { text: "PMS ID", value: "pms_id", sortable: true },
        { text: this.$t("status"), value: "status", sortable: true },
        { text: "Key", value: "key", sortable: true },
        { text: this.$t("room_type"), value: "room_type", sortable: true },
        { text: "Check-in", value: "check_in", sortable: true },
        { text: "Check-out", value: "check_out", sortable: true },
        { text: "Pax", value: "pax", sortable: true },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "center",
          width: "50px"
        }
      ];
    },
    audit_matched_headers() {
      return [
        { text: "PMS ID", value: "pms_id", sortable: true },
        { text: this.$t("status"), value: "status", sortable: true },
        { text: this.$t("room_type"), value: "room_type", sortable: true },
        { text: "Check-in", value: "check_in", sortable: true },
        { text: "Check-out", value: "check_out", sortable: true },
        { text: "Pax", value: "pax", sortable: true }
      ];
    },
    audit_mismatch_headers() {
      return [
        { text: "PMS ID", value: "pms_id", sortable: true },
        {
          text: "RMS " + this.$t("status"),
          value: "rms_status",
          sortable: true
        },
        {
          text: "PMS " + this.$t("status"),
          value: "pms_status",
          sortable: true
        },
        { text: this.$t("room_type"), value: "room_type", sortable: true },
        { text: "Check-in", value: "check_in", sortable: true },
        { text: "Check-out", value: "check_out", sortable: true },
        { text: "Pax", value: "pax", sortable: true }
      ];
    },
    revenueAuditDiscrepancyCount() {
      if (!this.revenue_audit_data || !this.revenue_audit_data.room_types)
        return 0;
      return this.revenue_audit_data.room_types.reduce((count, rt) => {
        if (!rt.reservations) return count;
        return count + rt.reservations.filter(r => r.has_discrepancy).length;
      }, 0);
    },
    ...mapGetters({
      current_hotel: "current_hotel"
    })
  },
  methods: {
    // Missatge d'error per les crides que consulten el PMS en directe, distingint
    // el timeout del client (axios) de qualsevol altre error.
    requestErrorMessage(error) {
      const timed_out =
        error &&
        (error.code === "ECONNABORTED" ||
          (error.message || "").indexOf("timeout") !== -1);
      if (timed_out) {
        return this.$t("data_validation_timeout");
      }
      return (
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.message) ||
        this.$t("data_validation_error")
      );
    },
    validate() {
      this.loading = true;
      this.validation_data = null;
      this.error_message = null;
      this.$store
        .dispatch("getDataValidation", {
          hotel_id: this.current_hotel.id,
          start_date: this.start_date,
          end_date: this.end_date,
          source: this.isUlysesPms ? this.validation_source : undefined
        })
        .then(response => {
          if (response && response.status === "error") {
            this.error_message =
              response.message || this.$t("data_validation_error");
            this.validation_data = null;
          } else {
            this.validation_data = response;
            this.initExpandedKpis();
          }
          this.loading = false;
        })
        .catch(error => {
          this.validation_data = null;
          this.error_message = this.requestErrorMessage(error);
          this.loading = false;
        });
    },
    dayLabel(day) {
      // Show only day number from "YYYY-MM-DD"
      const parts = day.split("-");
      return parseInt(parts[2], 10);
    },
    getDayValue(kpi_key, day, source) {
      if (
        !this.validation_data ||
        !this.validation_data.days ||
        !this.validation_data.days[day]
      ) {
        return null;
      }
      const day_data = this.validation_data.days[day];
      if (!day_data[kpi_key]) return null;
      return day_data[kpi_key][source];
    },
    getDayMatch(kpi_key, day) {
      if (
        !this.validation_data ||
        !this.validation_data.days ||
        !this.validation_data.days[day]
      ) {
        return null;
      }
      const day_data = this.validation_data.days[day];
      if (!day_data[kpi_key]) return null;
      return day_data[kpi_key].match;
    },
    getDayMatchColor(kpi_key, day) {
      const match = this.getDayMatch(kpi_key, day);
      if (match === null || match === undefined) return "grey";
      if (match >= 99.9) return "green";
      if (match >= 95) return "orange";
      return "red";
    },
    getDayMatchLabel(kpi_key, day) {
      const match = this.getDayMatch(kpi_key, day);
      if (match === null || match === undefined) return "-";
      return match.toFixed(1) + "%";
    },
    getPeriodMatch(kpi_key) {
      if (
        !this.validation_data ||
        !this.validation_data.period ||
        !this.validation_data.period[kpi_key]
      ) {
        return null;
      }
      return this.validation_data.period[kpi_key].match;
    },
    getPeriodMatchColor(kpi_key) {
      const match = this.getPeriodMatch(kpi_key);
      if (match === null || match === undefined) return "grey";
      if (match >= 99.9) return "green";
      if (match >= 95) return "orange";
      return "red";
    },
    getPeriodMatchLabel(kpi_key) {
      const match = this.getPeriodMatch(kpi_key);
      if (match === null || match === undefined) return "-";
      return match.toFixed(1) + "%";
    },
    getPeriodValue(kpi_key, source) {
      if (
        !this.validation_data ||
        !this.validation_data.period ||
        !this.validation_data.period[kpi_key]
      ) {
        return null;
      }
      return this.validation_data.period[kpi_key][source];
    },
    getPeriodDiff(kpi_key) {
      const pms = this.getPeriodValue(kpi_key, "pms");
      const veetal = this.getPeriodValue(kpi_key, "veetal");
      if (
        pms === null ||
        pms === undefined ||
        veetal === null ||
        veetal === undefined
      )
        return null;
      return Math.abs(parseFloat(veetal) - parseFloat(pms));
    },
    formatKpiValue(kpi, value) {
      if (value === null || value === undefined) return "-";
      if (kpi.type === "percent") {
        return parseFloat(value).toFixed(2) + "%";
      }
      if (kpi.type === "currency") {
        return parseFloat(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      return value;
    },
    setMonth(year, month) {
      const m = String(month).padStart(2, "0");
      this.start_date = `${year}-${m}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      this.end_date = `${year}-${m}-${String(lastDay).padStart(2, "0")}`;
    },
    goToPreviousMonth() {
      const d = new Date(this.start_date + "T00:00:00");
      d.setMonth(d.getMonth() - 1);
      this.setMonth(d.getFullYear(), d.getMonth() + 1);
    },
    goToNextMonth() {
      const d = new Date(this.start_date + "T00:00:00");
      d.setMonth(d.getMonth() + 1);
      this.setMonth(d.getFullYear(), d.getMonth() + 1);
    },
    onKpiScroll(e) {
      if (this.syncing_scroll) return;
      this.syncing_scroll = true;
      const scrollLeft = e.target.scrollLeft;
      const containers = this.$el.querySelectorAll(".kpi-scroll-container");
      containers.forEach(container => {
        if (container !== e.target) {
          container.scrollLeft = scrollLeft;
        }
      });
      this.syncing_scroll = false;
    },
    toggleKpi(key) {
      this.$set(this.expanded_kpis, key, !this.expanded_kpis[key]);
    },
    initExpandedKpis() {
      const expanded = {};
      for (const kpi of this.kpi_definitions) {
        const match = this.getPeriodMatch(kpi.key);
        expanded[kpi.key] =
          match === null || match === undefined || match < 99.9;
      }
      this.expanded_kpis = expanded;
    },
    updateEndDateFromStart() {
      if (!this.start_date) return;
      const d = new Date(this.start_date + "T00:00:00");
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const lastDay = new Date(year, month, 0).getDate();
      this.end_date = `${year}-${String(month).padStart(2, "0")}-${String(
        lastDay
      ).padStart(2, "0")}`;
    },
    isClickableKpi(kpi_key) {
      return (
        this.reservations_kpis.includes(kpi_key) ||
        this.availability_kpis.includes(kpi_key) ||
        this.revenue_kpis.includes(kpi_key)
      );
    },
    onCellClick(kpi_key, day) {
      if (this.reservations_kpis.includes(kpi_key)) {
        this.openDayReservations(day);
      } else if (this.availability_kpis.includes(kpi_key)) {
        this.openAvailabilityValidation(day);
      } else if (this.revenue_kpis.includes(kpi_key)) {
        this.openRevenueValidation(day);
      }
    },
    openRevenueValidation(day) {
      this.selected_revenue_day = day;
      this.revenue_reservations = [];
      this.revenue_tab = "rev-reservations";
      this.revenue_audit_data = null;
      this.revenue_audit_error = null;
      this.show_revenue_validation = true;
      this.fetchRevenueReservations();
    },
    closeRevenueValidation() {
      this.show_revenue_validation = false;
      this.selected_revenue_day = null;
      this.revenue_reservations = [];
      this.revenue_audit_data = null;
      this.revenue_audit_error = null;
      this.revenue_charges = [];
      this.revenue_tab = "rev-reservations";
      this.charges_service_type_filter = [];
      this.charges_in_production_filter = null;
      this.charges_in_adr_filter = null;
      this.expanded_audit_rows = {};
      this.reimporting_all_revenue = false;
    },
    fetchRevenueReservations() {
      this.loading_revenue = true;
      this.$store
        .dispatch("getDateReservations", {
          hotel_id: this.current_hotel.id,
          date: this.selected_revenue_day,
          page: 1,
          per_page: 9999,
          only_rn: true
        })
        .then(response => {
          const data = response.data || [];
          data.forEach((r, i) => {
            r._row_key = r.id + "_" + i;
          });
          this.revenue_reservations = data;
          this.loading_revenue = false;
        })
        .catch(() => {
          this.revenue_reservations = [];
          this.loading_revenue = false;
        });
    },
    fetchRevenueCharges() {
      if (this.loading_revenue_charges) return;
      this.loading_revenue_charges = true;
      this.revenue_charges = [];
      this.$store
        .dispatch("getRevenueCharges", {
          hotel_id: this.current_hotel.id,
          date: this.selected_revenue_day
        })
        .then(response => {
          const data = response.charges || [];
          data.forEach((c, i) => {
            c._row_key =
              (c.service || "") + "_" + (c.service_type || "") + "_" + i;
          });
          this.revenue_charges = data;
          this.loading_revenue_charges = false;
        })
        .catch(() => {
          this.revenue_charges = [];
          this.loading_revenue_charges = false;
        });
    },
    fetchRevenueAudit() {
      if (this.loading_revenue_audit) return;
      this.loading_revenue_audit = true;
      this.revenue_audit_data = null;
      this.revenue_audit_error = null;
      this.$store
        .dispatch("getRevenueAudit", {
          hotel_id: this.current_hotel.id,
          date: this.selected_revenue_day
        })
        .then(response => {
          this.revenue_audit_data = response;
          this.loading_revenue_audit = false;
        })
        .catch(error => {
          this.revenue_audit_data = null;
          this.revenue_audit_error = this.requestErrorMessage(error);
          this.loading_revenue_audit = false;
        });
    },
    openAvailabilityValidation(day) {
      this.selected_availability_day = day;
      this.availability_data = null;
      this.show_availability_validation = true;
      this.fetchAvailabilityValidation();
    },
    closeAvailabilityValidation() {
      this.show_availability_validation = false;
      this.selected_availability_day = null;
      this.availability_data = null;
    },
    fetchAvailabilityValidation() {
      this.loading_availability = true;
      this.$store
        .dispatch("getAvailabilityValidation", {
          hotel_id: this.current_hotel.id,
          date: this.selected_availability_day
        })
        .then(response => {
          this.availability_data = response;
          this.loading_availability = false;
        })
        .catch(() => {
          this.availability_data = null;
          this.loading_availability = false;
        });
    },
    openDayReservations(day) {
      this.selected_day = day;
      this.selected_reservation_id = null;
      this.day_reservations = [];
      this.day_reservations_total = 0;
      this.day_reservations_options = {
        page: 1,
        itemsPerPage: 25,
        sortBy: [],
        sortDesc: []
      };
      this.reservations_tab = 0;
      this.audit_data = null;
      this.audit_only_rn = true;
      this.reservation_status_filter = [];
      this.reservation_only_rn = false;
      this.all_reservation_statuses = [];
      this.show_day_reservations = true;
      this.fetchDayReservations();
    },
    closeDayReservations() {
      this.show_day_reservations = false;
      this.selected_day = null;
      this.selected_reservation_id = null;
      this.day_reservations = [];
      this.audit_data = null;
      this.reservations_tab = 0;
    },
    fetchAudit() {
      if (this.loading_audit) return;
      this.loading_audit = true;
      this.audit_data = null;
      this.$store
        .dispatch("getReservationsAudit", {
          hotel_id: this.current_hotel.id,
          date: this.selected_day,
          only_rn: this.audit_only_rn
        })
        .then(response => {
          this.audit_data = response;
          this.loading_audit = false;
        })
        .catch(() => {
          this.audit_data = null;
          this.loading_audit = false;
        });
    },
    refetchAudit() {
      this.fetchAudit();
    },
    onStatusFilterChange() {
      this.day_reservations_options.page = 1;
      this.fetchDayReservations();
    },
    onRnFilterChange() {
      this.day_reservations_options.page = 1;
      this.fetchDayReservations();
    },
    removeStatusFilter(index) {
      this.reservation_status_filter.splice(index, 1);
      this.onStatusFilterChange();
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return parseFloat(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    fetchDayReservations() {
      this.loading_day_reservations = true;
      const {
        page,
        itemsPerPage,
        sortBy,
        sortDesc
      } = this.day_reservations_options;
      this.$store
        .dispatch("getDateReservations", {
          hotel_id: this.current_hotel.id,
          date: this.selected_day,
          page: page,
          per_page: itemsPerPage,
          sort_by: sortBy.length > 0 ? sortBy[0] : null,
          sort_desc: sortDesc.length > 0 ? sortDesc[0] : null,
          statuses:
            this.reservation_status_filter.length > 0
              ? this.reservation_status_filter
              : null,
          only_rn: this.reservation_only_rn || null
        })
        .then(response => {
          const data = response.data || [];
          data.forEach((r, i) => {
            r._row_key = r.id + "_" + i;
          });
          this.day_reservations = data;
          this.day_reservations_total = response.total || 0;
          if (response.statuses) {
            this.all_reservation_statuses = response.statuses;
          }
          this.loading_day_reservations = false;
        })
        .catch(() => {
          this.day_reservations = [];
          this.day_reservations_total = 0;
          this.loading_day_reservations = false;
        });
    },
    reimportAllReservations() {
      if (!this.audit_data) return;
      const pms_ids_set = new Set();
      if (this.audit_data.only_rms) {
        this.audit_data.only_rms.forEach(r => {
          if (r.pms_id) pms_ids_set.add(r.pms_id);
        });
      }
      if (this.audit_data.only_pms) {
        this.audit_data.only_pms.forEach(r => {
          if (r.pms_id) pms_ids_set.add(r.pms_id);
        });
      }
      const pms_ids = [...pms_ids_set];
      if (!pms_ids.length) return;
      this.reimporting_all = true;
      this.$store
        .dispatch("reimportMultipleReservations", {
          hotel_id: this.current_hotel.id,
          pms_ids: pms_ids
        })
        .then(response => {
          this.reimporting_all = false;
          if (response && response.status === "ok") {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("reimport_all"),
              text: this.$t("reimport_all_success", { count: pms_ids.length }),
              color: "success"
            });
          } else {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("reimport_all"),
              text:
                response && response.message
                  ? response.message
                  : this.$t("reimport_error"),
              color: "error"
            });
          }
        })
        .catch(() => {
          this.reimporting_all = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("reimport_all"),
            text: this.$t("reimport_error"),
            color: "error"
          });
        });
    },
    reimportReservation(pms_id) {
      this.reimporting_pms_id = pms_id;
      this.$store
        .dispatch("reimportReservation", {
          hotel_id: this.current_hotel.id,
          pms_id: pms_id
        })
        .then(response => {
          this.reimporting_pms_id = null;
          if (response && response.status === "ok") {
            this.$store.dispatch("setSnackbar", {
              title: pms_id,
              text: this.$t("reimport_success"),
              color: "success"
            });
          } else {
            this.$store.dispatch("setSnackbar", {
              title: pms_id,
              text:
                response && response.message
                  ? response.message
                  : this.$t("reimport_error"),
              color: "error"
            });
          }
        })
        .catch(() => {
          this.reimporting_pms_id = null;
          this.$store.dispatch("setSnackbar", {
            title: pms_id,
            text: this.$t("reimport_error"),
            color: "error"
          });
        });
    },
    reimportAllRevenueAuditReservations() {
      if (!this.revenue_audit_data || !this.revenue_audit_data.room_types)
        return;
      const pms_ids_set = new Set();
      this.revenue_audit_data.room_types.forEach(rt => {
        if (!rt.reservations) return;
        rt.reservations.forEach(r => {
          if (r.has_discrepancy && r.pms_id) {
            pms_ids_set.add(r.pms_id);
          }
        });
      });
      const pms_ids = [...pms_ids_set];
      if (!pms_ids.length) return;
      this.reimporting_all_revenue = true;
      this.$store
        .dispatch("reimportMultipleReservations", {
          hotel_id: this.current_hotel.id,
          pms_ids: pms_ids
        })
        .then(response => {
          this.reimporting_all_revenue = false;
          if (response && response.status === "ok") {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("reimport_all"),
              text: this.$t("reimport_all_success", { count: pms_ids.length }),
              color: "success"
            });
          } else {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("reimport_all"),
              text:
                response && response.message
                  ? response.message
                  : this.$t("reimport_error"),
              color: "error"
            });
          }
        })
        .catch(() => {
          this.reimporting_all_revenue = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("reimport_all"),
            text: this.$t("reimport_error"),
            color: "error"
          });
        });
    },
    openReservationDetail(row) {
      this.selected_reservation_id = row.id;
    },
    getKpiDef(kpi_key) {
      return (
        this.kpi_definitions.find(k => k.key === kpi_key) || {
          key: kpi_key,
          type: "currency"
        }
      );
    },
    dayHasMismatch(kpi_key, day) {
      const match = this.getDayMatch(kpi_key, day);
      return match !== null && match !== undefined && match < 99.9;
    },
    promptRecalculateDay(day) {
      this.recalculate_day = day;
      this.show_recalculate_confirm = true;
    },
    cancelRecalculateDay() {
      this.show_recalculate_confirm = false;
      this.recalculate_day = null;
    },
    confirmRecalculateDay() {
      const day = this.recalculate_day;
      this.recalculating_day = day;
      this.$store
        .dispatch("generatePeriodKpis", {
          hotel_id: this.current_hotel.id,
          from_date: day,
          to_date: day,
          do_not_save: 0
        })
        .then(response => {
          this.recalculating_day = null;
          this.show_recalculate_confirm = false;
          this.recalculate_day = null;
          if (response && response.status === "error") {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("recalculate_kpis"),
              text: response.message || this.$t("recalculate_kpis_error"),
              color: "error"
            });
          } else {
            this.$store.dispatch("setSnackbar", {
              title: this.$t("recalculate_kpis"),
              text: this.$t("recalculate_kpis_success", { day: day }),
              color: "success"
            });
            this.validate();
          }
        })
        .catch(() => {
          this.recalculating_day = null;
          this.show_recalculate_confirm = false;
          this.recalculate_day = null;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("recalculate_kpis"),
            text: this.$t("recalculate_kpis_error"),
            color: "error"
          });
        });
    },
    toggleAuditRow(code) {
      this.$set(
        this.expanded_audit_rows,
        code,
        !this.expanded_audit_rows[code]
      );
    },
    expandAllAuditRows() {
      if (!this.revenue_audit_data || !this.revenue_audit_data.room_types)
        return;
      const expanded = {};
      this.revenue_audit_data.room_types.forEach(rt => {
        if (rt.reservations && rt.reservations.length) {
          expanded[rt.code] = true;
        }
      });
      this.expanded_audit_rows = expanded;
    },
    collapseAllAuditRows() {
      this.expanded_audit_rows = {};
    },
    formatRevenueDiff(diff) {
      if (diff === null || diff === undefined) return "-";
      const sign = diff > 0 ? "+" : "";
      return (
        sign +
        parseFloat(diff).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      );
    },
    discrepancyIcon(type) {
      if (type === "only_rms") return "mdi-arrow-right-bold";
      if (type === "only_pms") return "mdi-arrow-left-bold";
      if (type === "revenue_mismatch") return "mdi-not-equal-variant";
      return "mdi-help-circle";
    },
    discrepancyIconColor(type) {
      if (type === "only_rms") return "orange darken-2";
      if (type === "only_pms") return "red";
      if (type === "revenue_mismatch") return "deep-orange";
      return "grey";
    },
    discrepancyLabel(type) {
      if (type === "only_rms") return this.$t("only_rms");
      if (type === "only_pms") return this.$t("only_pms");
      if (type === "revenue_mismatch") return this.$t("revenue_mismatch");
      return "-";
    },
    initDates() {
      const now = new Date();
      this.setMonth(now.getFullYear(), now.getMonth() + 1);
    }
  },
  watch: {
    start_date() {
      this.updateEndDateFromStart();
    },
    day_reservations_options: {
      handler() {
        if (this.show_day_reservations && this.selected_day) {
          this.fetchDayReservations();
        }
      },
      deep: true
    },
    reservations_tab(val) {
      if (val === 1 && this.selectedDayHasMismatch && !this.audit_data) {
        this.fetchAudit();
      }
    },
    revenue_tab(val) {
      if (val === "rev-audit" && !this.revenue_audit_data) {
        this.fetchRevenueAudit();
      }
      if (
        val === "rev-charges" &&
        !this.revenue_charges.length &&
        !this.loading_revenue_charges
      ) {
        this.fetchRevenueCharges();
      }
    }
  },
  mounted() {
    this.initDates();
  }
};
</script>

<style scoped>
.validation-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;
}
.validation-table th,
.validation-table td {
  padding: 4px 6px;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}
.validation-table thead th {
  background-color: #f5f5f5;
  font-weight: 600;
}
.validation-table .source-col {
  min-width: 70px;
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}
.validation-table thead .source-col {
  background-color: #f5f5f5;
}
.validation-table .day-col {
  min-width: 60px;
}
.validation-table .match-row td {
  border-top: 2px solid #bdbdbd;
}
.validation-table .clickable-cell {
  cursor: pointer;
  color: #1976d2;
}
.validation-table .clickable-cell:hover {
  background-color: #e3f2fd;
}
.kpi-header {
  cursor: pointer;
  user-select: none;
}
.kpi-header:hover {
  background-color: #fafafa;
}
.validation-table .highlighted-day {
  background-color: #fff8e1 !important;
}
.validation-table thead .highlighted-day {
  background-color: #ffecb3 !important;
  font-weight: 700;
}

.day-header-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.recalculate-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
}
.recalculate-icon:hover {
  opacity: 1;
}
.availability-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}
.availability-table th,
.availability-table td {
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}
.availability-table .group-header {
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.availability-table .rms-group {
  background-color: #e3f2fd;
  color: #1565c0;
}
.availability-table .pms-group {
  background-color: #fce4ec;
  color: #c62828;
}
.availability-table .rms-col {
  background-color: #f5f9ff;
  font-weight: 600;
  text-align: center;
}
.availability-table .pms-col {
  background-color: #fff5f5;
  font-weight: 600;
  text-align: center;
}
.availability-table .room-type-col {
  min-width: 140px;
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}
.availability-table thead .room-type-col {
  background-color: #f5f5f5;
  z-index: 2;
}
.availability-table .data-col {
  min-width: 55px;
}
.availability-table .totals-row {
  border-top: 2px solid #bdbdbd;
}
.availability-table .totals-row td {
  background-color: #fafafa;
}

/* Revenue audit table */
.revenue-audit-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
}
.revenue-audit-table th,
.revenue-audit-table td {
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}
.revenue-audit-table .group-header {
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.revenue-audit-table .rms-group {
  background-color: #e3f2fd;
  color: #1565c0;
}
.revenue-audit-table .pms-group {
  background-color: #fce4ec;
  color: #c62828;
}
.revenue-audit-table .rms-col {
  background-color: #f5f9ff;
  font-weight: 600;
  text-align: center;
}
.revenue-audit-table .pms-col {
  background-color: #fff5f5;
  font-weight: 600;
  text-align: center;
}
.revenue-audit-table .room-type-col {
  min-width: 140px;
}
.revenue-audit-table thead .room-type-col {
  background-color: #f5f5f5;
}
.revenue-audit-table .expand-col {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  padding: 4px;
}
.revenue-audit-table .data-col {
  min-width: 70px;
}
.revenue-audit-table .totals-row {
  border-top: 2px solid #bdbdbd;
}
.revenue-audit-table .totals-row td {
  background-color: #fafafa;
}

.audit-room-type-row {
  cursor: pointer;
  transition: background-color 0.15s;
}
.audit-room-type-row:hover {
  background-color: #f5f5f5;
}
.audit-discrepancy-row {
  background-color: #fff8e1;
}
.audit-discrepancy-row:hover {
  background-color: #fff3c4 !important;
}
.audit-expanded {
  font-weight: 600;
}

/* Nested reservations table */
.audit-reservations-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;
  background-color: #fafafa;
}
.audit-reservations-table th {
  padding: 4px 8px;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  color: #616161;
}
.audit-reservations-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
}
.audit-reservations-table tbody tr:hover {
  background-color: #f5f5f5;
}
.audit-res-discrepancy {
  background-color: #fff8e1;
}
.audit-res-only-rms {
  border-left: 3px solid #e65100;
}
.audit-res-only-pms {
  border-left: 3px solid #c62828;
}
.audit-res-mismatch {
  border-left: 3px solid #e64a19;
}
.audit-detail-row > td {
  background-color: #fafafa;
  padding: 0 !important;
  border: none;
}
</style>
